import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminNewsArticle } from '@/lib/api'
import { parseContent } from '@/lib/contentBlocks'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

const SITE_DOMAIN = 'localidolculture.com'

function processContent(html: string): string {
  const normalizedHtml = html.replace(/<img\b[^>]*>/g, (tag) =>
    tag.replace(/\bsrc=("|')([^"']*)\1/i, (_match, quote: string, src: string) =>
      `src=${quote}${normalizeImageKitUrl(src).replace(/&/g, '&amp;')}${quote}`,
    ),
  )
  return normalizedHtml.replace(/<img([^>]*)>/g, (match, attrs: string) => {
    const captionMatch = attrs.match(/\sdata-caption="([^"]*)"/)
    if (!captionMatch) return match
    const caption = captionMatch[1]
    const cleanAttrs = attrs.replace(/\sdata-caption="[^"]*"/, '')
    const align = attrs.match(/data-align="([^"]*)"/)?.[1] ?? null
    const width = parseInt(attrs.match(/\swidth="(\d+)"/)?.[1] ?? '') || null
    let figureStyle = ''
    if (align === 'center') {
      figureStyle = ` style="text-align:center${width ? `;max-width:${width}px;margin:0 auto` : ''}"`
    } else if (align === 'right') {
      figureStyle = ` style="text-align:right${width ? `;max-width:${width}px;margin-left:auto` : ''}"`
    } else if (width) {
      figureStyle = ` style="max-width:${width}px"`
    }
    return `<figure${figureStyle}><img${cleanAttrs}><figcaption>${caption}</figcaption></figure>`
  })
}

const trunc = (s: string, n: number) => s.length > n ? s.slice(0, n) + '…' : s

function SourceBadge({ custom, fallbackFrom }: { custom: boolean; fallbackFrom?: string }) {
  return custom ? (
    <span className="text-[10px] px-1.5 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/20 whitespace-nowrap">
      Custom
    </span>
  ) : (
    <span className="text-[10px] px-1.5 py-0.5 rounded border bg-zinc-700/40 text-zinc-400 border-zinc-600/30 whitespace-nowrap">
      {fallbackFrom ? `Fallback: dari ${fallbackFrom}` : 'Fallback'}
    </span>
  )
}

function SeoRow({ label, value, custom, fallbackFrom }: {
  label: string
  value: string | null | undefined
  custom: boolean
  fallbackFrom?: string
}) {
  return (
    <div className="py-2.5 border-b border-zinc-800 last:border-0 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-zinc-500 tracking-wide uppercase mb-0.5">{label}</p>
        {value
          ? <p className="text-sm text-zinc-200 leading-relaxed">{value}</p>
          : <p className="text-xs text-zinc-600 italic">—</p>
        }
      </div>
      <div className="flex-shrink-0 mt-4">
        <SourceBadge custom={custom} fallbackFrom={fallbackFrom} />
      </div>
    </div>
  )
}

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()
  const { confirm, dialog } = useConfirm()

  const [article, setArticle] = useState<AdminNewsArticle | null>(
    (location.state as { article?: AdminNewsArticle } | null)?.article ?? null
  )
  const [loading, setLoading] = useState(!article)
  const [tab, setTab] = useState<'article' | 'seo'>('article')

  useEffect(() => {
    if (article || !token || !id) return
    api.getAdminNews(token)
      .then((items) => setArticle(items.find((a) => a.id === id) ?? null))
      .finally(() => setLoading(false))
  }, [article, id, token])

  const handleDelete = async () => {
    if (!article) return
    if (!await confirm(
      `"${article.title}" akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${article.title}"` }
    )) return
    if (!token) return
    try {
      await api.deleteNews(token, article.id)
      navigate(-1)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (!article) {
    return <div className="p-6"><p className="text-red-400 text-sm">Article not found.</p></div>
  }

  const metaTitle = article.seo_title || article.title
  const metaDesc  = article.seo_description || article.text || ''
  const ogTitle   = article.og_title || metaTitle
  const ogDesc    = article.og_description || metaDesc
  const ogImage   = article.og_image_url || article.image_url
  const serpTitle = `${metaTitle} | LIC`

  return (
    <div className="p-6">
      {/* Nav */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft size={14} />
          Kembali
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/news/${article.id}/edit`, { state: { article } })}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded hover:bg-zinc-800"
          >
            Hapus
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden bg-zinc-800 mb-5">
        <div className="aspect-[2/1] w-full relative">
          {article.image_url ? (
            <img
              src={normalizeImageKitUrl(article.image_url)}
              alt={article.title}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-800 min-h-[120px]">
              <span className="text-zinc-700 text-xs">Tidak ada gambar</span>
            </div>
          )}

          {/* Badges — top left */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm bg-black/30 text-white/80 border border-white/10">
              {article.category}
            </span>
            {article.is_featured && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm bg-yellow-500/20 text-yellow-300 border border-yellow-500/20">
                ★ Featured
              </span>
            )}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm border ${
              !article.is_draft
                ? 'bg-green-500/20 text-green-300 border-green-500/20'
                : 'bg-black/30 text-white/40 border-white/10'
            }`}>
              {!article.is_draft ? 'Published' : 'Draft'}
            </span>
          </div>
        </div>

        {/* Glassmorphism title bar */}
        <div className="backdrop-blur-md bg-black/30 border-t border-white/10 px-5 py-3.5">
          <h1 className="text-base font-bold text-white leading-snug">{article.title}</h1>
          <p className="text-white/45 text-xs mt-0.5">{article.date}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-zinc-800 mb-5">
        {(['article', 'seo'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
              tab === t
                ? 'border-yellow-500 text-white'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {t === 'article' ? 'Artikel' : 'SEO'}
          </button>
        ))}
      </div>

      {/* ── Article tab ─────────────────────────────────────────────── */}
      {tab === 'article' && (
        <div className="space-y-4">
          {article.text && (
            <p className="text-zinc-400 text-sm border-l-2 border-zinc-700 pl-3 italic leading-relaxed">
              {article.text}
            </p>
          )}

          {article.content.trim().startsWith('<') ? (
            <div className="pt-2 tiptap-content">
              <div className="ProseMirror !min-h-0" dangerouslySetInnerHTML={{ __html: processContent(article.content) }} />
            </div>
          ) : (
            <div className="pt-2 space-y-4">
              {parseContent(article.content).map((block, i) => {
                if (block.type === 'image') {
                  return (
                    <figure key={i}>
                      <img src={normalizeImageKitUrl(block.url)} alt={block.caption || ''} className="w-full rounded" />
                      {block.caption && (
                        <figcaption className="mt-1.5 text-xs text-zinc-500 italic">{block.caption}</figcaption>
                      )}
                    </figure>
                  )
                }
                return (
                  <p key={i} className="text-sm text-zinc-300 leading-relaxed">{block.text}</p>
                )
              })}
            </div>
          )}

          <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden mt-6">
            <div className="px-5 py-3 border-b border-zinc-800">
              <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Info Teknis</h2>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-zinc-500 mb-1">Slug</p>
                <p className="text-xs text-zinc-400 font-mono break-all">{article.slug}</p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 mb-1">Article ID</p>
                <p className="text-xs text-zinc-600 font-mono break-all">{article.id}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SEO tab ─────────────────────────────────────────────────── */}
      {tab === 'seo' && (
        <div className="space-y-6">

          {/* Search Preview */}
          <div>
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Search Preview</p>
            <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4">
              <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                <span className="text-sm leading-none">🌐</span>
                <span className="text-xs text-[#34a853]">{SITE_DOMAIN}</span>
                <span className="text-xs text-zinc-600">›</span>
                <span className="text-xs text-[#34a853]">news</span>
                <span className="text-xs text-zinc-600">›</span>
                <span className="text-xs text-[#34a853] truncate max-w-[200px]">{article.slug}</span>
              </div>
              <p className="text-[#8ab4f8] text-base font-medium leading-snug mb-1">
                {trunc(serpTitle, 60)}
                {serpTitle.length > 60 && (
                  <span className="ml-1 text-[10px] text-zinc-600">({serpTitle.length} karakter)</span>
                )}
              </p>
              <p className="text-zinc-400 text-xs leading-relaxed">
                {metaDesc
                  ? trunc(metaDesc, 155)
                  : <span className="italic text-zinc-600">Tidak ada deskripsi</span>
                }
              </p>
              <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Robots:</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
                  article.seo_no_index
                    ? 'bg-red-500/10 text-red-400 border-red-500/20'
                    : 'bg-green-500/10 text-green-400 border-green-500/20'
                }`}>
                  {article.seo_no_index ? 'noindex, nofollow' : 'index, follow'}
                </span>
              </div>
            </div>
          </div>

          {/* Social/OG Preview */}
          <div>
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Social Preview</p>
            <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden max-w-sm">
              {ogImage ? (
                <img src={normalizeImageKitUrl(ogImage)} alt="" className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-36 bg-zinc-800 flex items-center justify-center">
                  <span className="text-zinc-600 text-xs">Tidak ada gambar</span>
                </div>
              )}
              <div className="p-3 border-t border-zinc-800">
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-0.5">{SITE_DOMAIN}</p>
                <p className="text-sm font-semibold text-zinc-100 leading-snug line-clamp-2">{ogTitle}</p>
                {ogDesc && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{ogDesc}</p>}
              </div>
            </div>
          </div>

          {/* Field Breakdown */}
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 px-4">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase pt-4 pb-1">Field Breakdown</p>
            <SeoRow
              label="SEO Title"
              value={metaTitle}
              custom={!!article.seo_title}
              fallbackFrom={article.seo_title ? undefined : 'judul artikel'}
            />
            <SeoRow
              label="SEO Description"
              value={metaDesc || null}
              custom={!!article.seo_description}
              fallbackFrom={article.seo_description ? undefined : 'lead text'}
            />
            <SeoRow
              label="OG Title"
              value={ogTitle}
              custom={!!article.og_title}
              fallbackFrom={article.og_title ? undefined : 'SEO title'}
            />
            <SeoRow
              label="OG Description"
              value={ogDesc || null}
              custom={!!article.og_description}
              fallbackFrom={article.og_description ? undefined : 'SEO description'}
            />
            <SeoRow
              label="OG Image"
              value={ogImage ? 'Ada' : null}
              custom={!!article.og_image_url}
              fallbackFrom={article.og_image_url ? undefined : 'cover image artikel'}
            />
          </div>
        </div>
      )}

      </div>
      {dialog}
    </div>
  )
}
